import Template from "../../components/Template";
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import patrimonioService from "../../services/patrimonioService";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { Link } from "react-router-dom";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";
import { formatCurrencyFromDb } from "../../utils/masks";
import { formatDateWithoutHour } from "../../utils/date";
import MovimentarPatrimonioModal from "./MovimentarPatrimonioModal";
import { TIPOS_PATRIMONIO, SITUACOES_PATRIMONIO, getTipoLabel, getLocalizacaoLabel, SituacaoBadge, getSituacaoInfo, isBaixado } from "./patrimonioConstants";

const PatrimonioList = () => {
    const navigate = useNavigate();
    const podeCriar = usePermission("CRIAR_PATRIMONIO");
    const podeEditar = usePermission("EDITAR_PATRIMONIO");
    const podeExcluir = usePermission("EXCLUIR_PATRIMONIO");
    const podeMovimentar = usePermission("MOVIMENTAR_PATRIMONIO");
    const podeGerenciarLocais = usePermission("GERENCIAR_LOCAIS_PATRIMONIO");
    const podeGerenciarCategorias = usePermission("GERENCIAR_CATEGORIAS_PATRIMONIO");

    const [locais, setLocais] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [obras, setObras] = useState([]);
    const [patrimonios, setPatrimonios] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");
    const appliedFiltersRef = useRef({});

    // Modal de movimentação
    const [patrimonioParaMovimentar, setPatrimonioParaMovimentar] = useState(null);

    const { filters, setFilters, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('patrimonios', {
        search: "",
        localId: "",
        obraId: "",
        categoriaId: "",
        tipo: "",
        situacao: "",
        responsavel: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [locaisRes, categoriasRes, obrasRes] = await Promise.all([
                    patrimonioService.listarLocais(),
                    patrimonioService.listarCategorias(),
                    api.get("/obras/listar/todas"),
                ]);
                setLocais(locaisRes || []);
                setCategorias(categoriasRes || []);
                setObras(obrasRes.data || []);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchPatrimonios(1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPatrimonios = async (page, size) => {
        try {
            const data = await patrimonioService.listar({
                ...appliedFiltersRef.current,
                page,
                pageSize: size,
            });
            setPatrimonios(data.records || []);
            setTotalRecords(data.total || 0);
        } catch (error) {
            console.error("Erro ao carregar patrimônios:", error);
        }
    };

    const handleEditarClick = (patrimonioId) => {
        navigate(`/patrimonios/${patrimonioId}/editar`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchPatrimonios(page, pageSize);
    };

    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    const pageWindowSize = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from(
        { length: Math.max(endPage - startPage + 1, 0) },
        (_, i) => startPage + i
    );

    const [columns, setColumns] = useState([
        {
            id: "codigo",
            label: "Código",
            visible: true,
            formatter: (value, patrimonio) => (
                <Link to={`/patrimonios/${patrimonio.id}/editar`} className="text-stone-600 hover:underline font-medium">
                    {value}
                </Link>
            ),
        },
        {
            id: "nome",
            label: "Nome",
            visible: true,
            formatter: (value) => value || "",
        },
        {
            id: "tipo",
            label: "Tipo",
            visible: true,
            formatter: (value) => getTipoLabel(value),
        },
        {
            id: "categoria",
            label: "Categoria",
            visible: true,
            formatter: (value) => value?.nome || "-",
        },
        {
            id: "localizacao",
            label: "Localização",
            visible: true,
            formatter: (value, patrimonio) => getLocalizacaoLabel(patrimonio),
        },
        {
            id: "responsavel",
            label: "Responsável",
            visible: true,
            formatter: (value) => value || "-",
        },
        {
            id: "situacao",
            label: "Situação",
            visible: true,
            formatter: (value) => <SituacaoBadge situacao={value} />,
        },
        {
            id: "valorAquisicao",
            label: "Valor Aquisição (R$)",
            visible: true,
            formatter: (value) => formatCurrencyFromDb(value) || "-",
        },
        {
            id: "valorAtual",
            label: "Valor Atual (R$)",
            visible: true,
            formatter: (value) => formatCurrencyFromDb(value) || "-",
        },
        {
            id: "dataAquisicao",
            label: "Data Aquisição",
            visible: false,
            formatter: (value) => value ? formatDateWithoutHour(value) : "-",
        },
        {
            id: "numeroSerie",
            label: "Nº Série",
            visible: false,
            formatter: (value) => value || "-",
        },
        {
            id: "placa",
            label: "Placa",
            visible: false,
            formatter: (value) => value || "-",
        },
        {
            id: "acoes",
            label: "Ações",
            visible: true,
            formatter: (value, patrimonio) => (
                <div className="flex gap-2 justify-center items-center" style={{ minWidth: "100px" }}>
                    {podeEditar && (
                        <img
                            src="/images/icon/edit.png"
                            alt="Editar"
                            title="Editar"
                            onClick={() => handleEditarClick(patrimonio.id)}
                            style={{ width: "20px", height: "20px", minWidth: "20px", minHeight: "20px", cursor: "pointer" }}
                        />
                    )}
                    {podeMovimentar && !isBaixado(patrimonio.situacao) && (
                        <img
                            src="/images/icon/truck.png"
                            alt="Movimentar"
                            title="Movimentar"
                            onClick={() => setPatrimonioParaMovimentar(patrimonio)}
                            style={{ width: "20px", height: "20px", minWidth: "20px", minHeight: "20px", cursor: "pointer" }}
                        />
                    )}
                    {podeExcluir && (
                        <img
                            src="/images/icon/trash.png"
                            alt="Excluir"
                            title="Excluir"
                            onClick={() => handleDelete(patrimonio.id)}
                            style={{ width: "20px", height: "20px", minWidth: "20px", minHeight: "20px", cursor: "pointer" }}
                        />
                    )}
                </div>
            ),
        },
    ]);

    const [menuOpen, setMenuOpen] = useState(null);

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    const handleMenuClick = (event) => {
        event.stopPropagation();
    };

    const applyFilters = () => {
        const query = {};

        if (filters.search) query.search = filters.search;
        if (filters.localId) query.localId = filters.localId;
        if (filters.obraId) query.obraId = filters.obraId;
        if (filters.categoriaId) query.categoriaId = filters.categoriaId;
        if (filters.tipo) query.tipo = filters.tipo;
        if (filters.situacao) query.situacao = filters.situacao;
        if (filters.responsavel) query.responsavel = filters.responsavel;

        appliedFiltersRef.current = query;
        setCurrentPage(1);
        fetchPatrimonios(1, pageSize);
        setMenuOpen(null);
    };

    const clearFilters = () => {
        clearPersistedState();
        setFilters({
            search: "",
            localId: "",
            obraId: "",
            categoriaId: "",
            tipo: "",
            situacao: "",
            responsavel: "",
        });
        appliedFiltersRef.current = {};
        setCurrentPage(1);
        fetchPatrimonios(1, pageSize);
        setMenuOpen(null);
    };

    const handleSearch = () => {
        const query = {};
        if (filters.search) query.search = filters.search;
        appliedFiltersRef.current = query;
        setCurrentPage(1);
        fetchPatrimonios(1, pageSize);
    };

    const exportToExcel = () => {
        if (patrimonios.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        const dataToExport = patrimonios.map((patrimonio) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible && col.id !== 'acoes') {
                    const value = patrimonio[col.id];
                    if (col.id === 'valorAquisicao' || col.id === 'valorAtual') {
                        row[col.label] = typeof value === 'number' ? value : parseFloat(value) || 0;
                    } else if (col.id === 'tipo') {
                        row[col.label] = getTipoLabel(value);
                    } else if (col.id === 'situacao') {
                        row[col.label] = getSituacaoInfo(value).label;
                    } else if (col.id === 'categoria') {
                        row[col.label] = value?.nome || '';
                    } else if (col.id === 'localizacao') {
                        row[col.label] = getLocalizacaoLabel(patrimonio);
                    } else if (col.id === 'dataAquisicao') {
                        row[col.label] = value ? formatDateWithoutHour(value) : '';
                    } else {
                        row[col.label] = value ?? '';
                    }
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Formato numérico para as colunas de valor
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const visibleCols = columns.filter(col => col.visible && col.id !== 'acoes');
        visibleCols.forEach((col, colIndex) => {
            if (col.id === 'valorAquisicao' || col.id === 'valorAtual') {
                for (let row = range.s.r + 1; row <= range.e.r; row++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });
                    if (worksheet[cellAddress] && typeof worksheet[cellAddress].v === 'number') {
                        worksheet[cellAddress].z = '#,##0.00';
                        worksheet[cellAddress].t = 'n';
                    }
                }
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Patrimônio");
        XLSX.writeFile(workbook, "patrimonio.xlsx");
    };

    const handleDelete = async (id) => {
        if (window.confirm("Deseja realmente excluir este bem do patrimônio?")) {
            try {
                await patrimonioService.excluir(id);
                setMessage("Patrimônio excluído com sucesso!");
                setShowSuccessDialog(true);
                fetchPatrimonios(currentPage, pageSize);
            } catch (error) {
                console.error("Erro ao excluir patrimônio:", error);
                alert(error.response?.data?.error || "Erro ao excluir patrimônio");
            }
        }
    };

    const handleMovimentacaoSuccess = () => {
        setPatrimonioParaMovimentar(null);
        setMessage("Movimentação registrada com sucesso!");
        setShowSuccessDialog(true);
        fetchPatrimonios(currentPage, pageSize);
    };

    return (
        <Template>
            <div className="h-auto w-full p-3 sm:p-4 md:p-8 flex flex-col gap-4 md:gap-6">
                {/* Título + Botões */}
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <div className="text-stone-800 text-lg sm:text-xl md:text-2xl font-medium font-['Inter']">Patrimônio</div>
                    <div className="flex items-center gap-2">
                        {podeGerenciarLocais && (
                            <button
                                type='button'
                                className="px-3 py-2 sm:px-4 bg-white rounded-md border border-stone-500 flex items-center gap-2"
                                onClick={() => navigate('/patrimonios/locais')}
                                title="Gerenciar Locais"
                            >
                                <img className="w-4 h-4" src="/images/icon/settings.png" alt="Locais" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal hidden sm:block">Locais</div>
                            </button>
                        )}
                        {podeGerenciarCategorias && (
                            <button
                                type='button'
                                className="px-3 py-2 sm:px-4 bg-white rounded-md border border-stone-500 flex items-center gap-2"
                                onClick={() => navigate('/patrimonios/categorias')}
                                title="Gerenciar Categorias"
                            >
                                <img className="w-4 h-4" src="/images/icon/sticker.png" alt="Categorias" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal hidden sm:block">Categorias</div>
                            </button>
                        )}
                        {podeCriar && (
                            <button
                                type='button'
                                className="px-3 py-2 sm:px-4 bg-stone-600 text-white rounded-md flex items-center gap-2"
                                onClick={() => navigate('/patrimonios/novo')}
                            >
                                <img className="w-4 h-4" src="/images/icon/plus-circle.png" alt="Cadastrar" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Cadastrar</div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-2">
                            <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Ver</div>
                            <select
                                className="w-16 sm:w-20 h-8 pl-2 pr-1 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                                value={pageSize}
                                onChange={(e) => {
                                    const newSize = parseInt(e.target.value, 10);
                                    setPageSize(newSize);
                                    setCurrentPage(1);
                                    fetchPatrimonios(1, newSize);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </select>
                            <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight hidden sm:block">Resultados</div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 ml-auto flex-wrap">
                            {/* Colunas (desktop only) */}
                            <div ref={buttonRef} className="relative items-center gap-1 hidden md:flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                                <div className="items-center gap-2 flex">
                                    <img className="w-6 h-6 relative" src="/images/icon/columns.png" alt="Colunas" />
                                    <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Colunas</div>
                                </div>
                                <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Expandir" />
                                {menuOpen === "colunas" && (
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg"
                                        style={{ zIndex: 9999 }}
                                        onClick={handleMenuClick}
                                    >
                                        <div className="p-4 flex flex-col gap-3">
                                            {columns.filter(col => col.id !== 'acoes').map((col) => (
                                                <div key={col.id} className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        id={col.id}
                                                        checked={col.visible}
                                                        onChange={() => toggleColumnVisibility(col.id)}
                                                    />
                                                    <label htmlFor={col.id} className="text-sm text-gray-700">
                                                        {col.label}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Filtrar */}
                            <div ref={buttonFiltrarRef} className="relative items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")}>
                                <div className="items-center gap-1 sm:gap-2 flex">
                                    <img className="w-7 h-7 sm:w-8 sm:h-8" src="/images/icon/filter.png" alt="Filtrar" />
                                    <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal hidden sm:block">Filtrar</div>
                                </div>
                                <img className="w-5 h-3 sm:w-6 sm:h-4 px-0.5" src="/images/icon/chevron-down.png" alt="Expandir" />
                                {menuOpen === "filtro" && (
                                    <div
                                        className="fixed inset-0 sm:absolute sm:inset-auto sm:top-full sm:right-0 sm:left-auto sm:mt-2 sm:w-[350px] bg-white sm:rounded-md shadow-lg p-4 border-0 sm:border sm:border-gray-300 overflow-y-auto"
                                        style={{ zIndex: 9999 }}
                                        onClick={handleMenuClick}
                                    >
                                        <div className="flex flex-col gap-4 max-w-lg mx-auto sm:max-w-none">
                                            <div className="flex justify-between items-center">
                                                <div className="text-stone-800 text-xl font-medium">Filtrar</div>
                                                <button className="sm:hidden text-stone-500 text-2xl px-2" onClick={() => setMenuOpen(null)}>&times;</button>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Busca livre</div>
                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    placeholder="Código, nome, nº série, placa..."
                                                    value={filters.search}
                                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Local</div>
                                                <select
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                    value={filters.localId}
                                                    onChange={(e) => setFilters({ ...filters, localId: e.target.value })}
                                                >
                                                    <option value="">Selecionar</option>
                                                    {locais.map((l) => (
                                                        <option key={l.id} value={l.id}>{l.nome}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Obra</div>
                                                <select
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                    value={filters.obraId}
                                                    onChange={(e) => setFilters({ ...filters, obraId: e.target.value })}
                                                >
                                                    <option value="">Selecionar</option>
                                                    {obras.map((o) => (
                                                        <option key={o.id} value={o.id}>{o.nome || o.nomeProjeto}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Tipo</div>
                                                <select
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                    value={filters.tipo}
                                                    onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                                                >
                                                    <option value="">Selecionar</option>
                                                    {TIPOS_PATRIMONIO.map((t) => (
                                                        <option key={t.value} value={t.value}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Categoria</div>
                                                <select
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                    value={filters.categoriaId}
                                                    onChange={(e) => setFilters({ ...filters, categoriaId: e.target.value })}
                                                >
                                                    <option value="">Selecionar</option>
                                                    {categorias
                                                        .filter((c) => !filters.tipo || c.tipo === filters.tipo)
                                                        .map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                                {filters.tipo ? c.nome : `${getTipoLabel(c.tipo)} - ${c.nome}`}
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Situação</div>
                                                <select
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                    value={filters.situacao}
                                                    onChange={(e) => setFilters({ ...filters, situacao: e.target.value })}
                                                >
                                                    <option value="">Selecionar</option>
                                                    {SITUACOES_PATRIMONIO.map((s) => (
                                                        <option key={s.value} value={s.value}>{s.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <div className="text-slate-700 text-sm font-medium leading-tight">Responsável</div>
                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    placeholder="Nome do responsável"
                                                    value={filters.responsavel}
                                                    onChange={(e) => setFilters({ ...filters, responsavel: e.target.value })}
                                                />
                                            </div>

                                            <div className="flex items-center justify-between gap-3 mt-2">
                                                <button className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 justify-center items-center gap-2 flex" onClick={clearFilters}>
                                                    <img className="w-4 h-4 relative" src="/images/icon/eraser.png" alt="Limpar" />
                                                    <div className="text-stone-500 text-sm font-medium font-['Inter'] leading-normal">Limpar</div>
                                                </button>
                                                <button className="px-4 py-2 w-full bg-stone-600 rounded-md justify-center items-center gap-2 flex" onClick={applyFilters}>
                                                    <img className="w-4 h-4 relative" src="/images/icon/filter-white.png" alt="Filtrar" />
                                                    <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Filtrar</div>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Pesquisar */}
                            <div className="flex items-center gap-2">
                                {!isSearchActive ? (
                                    <div className="items-center gap-1 sm:gap-2 flex cursor-pointer" onClick={() => setIsSearchActive(true)}>
                                        <img className="w-8 h-8 sm:w-10 sm:h-10 relative" src="/images/icon/search.png" alt="Pesquisar" />
                                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal hidden sm:block">Pesquisar</div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <div className="h-9 px-2 sm:px-3 py-2 bg-white rounded-md border border-slate-300 flex items-center">
                                            <input
                                                type="text"
                                                placeholder="Código, nome, nº série"
                                                className="w-28 sm:w-full text-slate-700 text-sm sm:text-base font-normal font-['Inter'] leading-tight focus:outline-none"
                                                value={filters.search}
                                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                                onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                                            />
                                            <img className="w-5 h-5 cursor-pointer ml-1" src="/images/icon/close.png" alt="Fechar"
                                                onClick={() => {
                                                    setIsSearchActive(false);
                                                    setFilters({ ...filters, search: "" });
                                                    appliedFiltersRef.current = {};
                                                    setCurrentPage(1);
                                                    fetchPatrimonios(1, pageSize);
                                                }}
                                            />
                                        </div>
                                        <button className="px-3 py-2 bg-stone-600 rounded-md flex items-center gap-1" onClick={handleSearch}>
                                            <img className="w-4 h-4" src="/images/icon/search-white.png" alt="Pesquisar" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Exportar */}
                            <button
                                type='button'
                                className="flex px-3 py-2 bg-white/40 rounded-md border border-stone-600 items-center gap-1 sm:gap-2 cursor-pointer"
                                onClick={exportToExcel}
                            >
                                <img className="w-4 h-4" src="/images/icon/upload.png" alt="Exportar" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal hidden sm:block">Exportar</div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* === TABELA (Desktop: md+) === */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                {columns
                                    .filter((col) => col.visible)
                                    .map((col) => (
                                        <th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">
                                            {col.label}
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {patrimonios.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.filter(col => col.visible).length} className="px-4 py-8 text-center text-slate-500 text-base">
                                        Nenhum bem encontrado
                                    </td>
                                </tr>
                            ) : (
                                patrimonios.map((patrimonio) => (
                                    <tr key={patrimonio.id}>
                                        {columns
                                            .filter((col) => col.visible)
                                            .map((col) => (
                                                <td key={`${patrimonio.id}-${col.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">
                                                    {col.formatter(patrimonio[col.id], patrimonio)}
                                                </td>
                                            ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* === CARDS (Mobile: < md) === */}
                <div className="md:hidden flex flex-col gap-3">
                    {patrimonios.length === 0 ? (
                        <div className="py-8 text-center text-slate-500 text-base bg-white rounded-lg border border-gray-200">
                            Nenhum bem encontrado
                        </div>
                    ) : (
                        patrimonios.map((patrimonio) => (
                            <div key={patrimonio.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                {/* Card Header */}
                                <div className="flex items-center justify-between px-4 py-3 bg-stone-50 border-b border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <Link to={`/patrimonios/${patrimonio.id}/editar`} className="text-stone-700 font-semibold text-sm hover:underline">
                                            {patrimonio.codigo}
                                        </Link>
                                        <SituacaoBadge situacao={patrimonio.situacao} />
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        {podeEditar && (
                                            <img src="/images/icon/edit.png" alt="Editar" className="w-5 h-5 cursor-pointer"
                                                onClick={() => handleEditarClick(patrimonio.id)} />
                                        )}
                                        {podeMovimentar && !isBaixado(patrimonio.situacao) && (
                                            <img src="/images/icon/truck.png" alt="Movimentar" className="w-5 h-5 cursor-pointer"
                                                onClick={() => setPatrimonioParaMovimentar(patrimonio)} />
                                        )}
                                        {podeExcluir && (
                                            <img src="/images/icon/trash.png" alt="Excluir" className="w-5 h-5 cursor-pointer"
                                                onClick={() => handleDelete(patrimonio.id)} />
                                        )}
                                    </div>
                                </div>
                                {/* Card Body */}
                                <div className="px-4 py-3 flex flex-col gap-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Nome</span>
                                        <span className="text-slate-700 font-medium text-right max-w-[60%] truncate">
                                            {patrimonio.nome}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Tipo</span>
                                        <span className="text-slate-700 font-medium">{getTipoLabel(patrimonio.tipo)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Localização</span>
                                        <span className="text-slate-700 font-medium text-right max-w-[60%] truncate">
                                            {getLocalizacaoLabel(patrimonio)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Responsável</span>
                                        <span className="text-slate-700 font-medium text-right max-w-[60%] truncate">
                                            {patrimonio.responsavel || '-'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-t border-gray-100 pt-2 mt-1">
                                        <span className="text-slate-500 font-medium">Valor Atual</span>
                                        <span className="text-stone-800 font-bold">
                                            {formatCurrencyFromDb(patrimonio.valorAtual) || '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* === PAGINAÇÃO === */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-slate-500 text-sm sm:text-base font-normal font-['Inter'] leading-relaxed text-center sm:text-left">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.
                    </div>
                    <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
                        <button
                            className={`w-9 h-9 sm:w-10 sm:h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${
                                currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                            }`}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-5 h-5 sm:w-6 sm:h-6 relative" src="/images/icon/keyboard-arrow-left.png" alt="Anterior" />
                        </button>

                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-9 h-9 sm:w-10 sm:h-10 rounded justify-center items-center flex text-sm sm:text-base font-semibold font-['Inter'] ${
                                    page === currentPage
                                        ? "bg-stone-600 text-white"
                                        : "bg-gray-200 text-gray-600"
                                }`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            className={`w-9 h-9 sm:w-10 sm:h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${
                                currentPage === totalPages || totalPages === 0 ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-5 h-5 sm:w-6 sm:h-6 relative" src="/images/icon/keyboard-arrow-right.png" alt="Próximo" />
                        </button>
                    </div>
                </div>

                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />

                <MovimentarPatrimonioModal
                    isVisible={!!patrimonioParaMovimentar}
                    patrimonio={patrimonioParaMovimentar}
                    locais={locais}
                    obras={obras}
                    onClose={() => setPatrimonioParaMovimentar(null)}
                    onSuccess={handleMovimentacaoSuccess}
                />
            </div>
        </Template>
    );
};

export default PatrimonioList;
